/**
 * Keep submitted personal data out of Sentry (EW-T18).
 *
 * Before this module, every GraphQL error was reported with the raw request variables
 * (`extra.variables`) AND as the graphql-request `ClientError` itself. That error's `message` is
 * `"<first error>: " + JSON.stringify({ response, request })` (graphql-request 7.4.0,
 * `ClientError.js`), and `request` carries the variables. So a refused `collectClientInputs`
 * shipped the director's PAN, Aadhaar, name, date of birth and address to Sentry twice, once in
 * `extra` and once in the exception text.
 *
 * Two layers:
 *  1. `graphqlErrorReport` builds what graphql_client sends: a fresh Error with no payload, the
 *     variables as a keys-only SHAPE (values replaced by their type), and the GraphQL errors
 *     reduced to message, path and error codes (no `extensions.data`).
 *  2. `scrubSentryEvent` is `beforeSend` defence in depth for every event, including ones this
 *     module never built. It cuts the embedded graphql-request payload out of any string and
 *     redacts known PII keys in extra, contexts, request data and breadcrumbs.
 */
import type { ErrorEvent, EventHint } from '@sentry/react';
import type { ClientError } from 'graphql-request';

export const REDACTED = '[Redacted]';

/**
 * Keys whose values are personal data in this app's payloads (client inputs, intake, uploads).
 * Matched case-insensitively. `value` covers `inputs[].value` of collectClientInput(s).
 */
const PII_KEYS = new Set([
  'value', 'clientdata', 'pan', 'aadhaar', 'dob', 'date_of_birth', 'dateofbirth', 'email', 'phone',
  'name', 'address', 'residential_address', 'residentialaddress', 'filename', 'variables',
]);

/** Sentry SDK contexts that describe the environment, not the user. Their `name` is not PII. */
const SDK_CONTEXTS = new Set(['os', 'browser', 'device', 'runtime', 'app', 'trace', 'culture', 'react', 'cloud_resource', 'response']);

/** Where graphql-request appends its JSON payload to a ClientError message. */
const GQL_PAYLOAD_MARKER = ': {"response":';

/** Cut graphql-request's `{"response":…,"request":…}` payload off a string, keeping its prefix. */
export function stripGraphqlPayload(s: string): string {
  const i = s.indexOf(GQL_PAYLOAD_MARKER);
  return i === -1 ? s : `${s.slice(0, i)} [graphql payload redacted]`;
}

const MAX_DEPTH = 8;
const MAX_ARRAY = 5;

/**
 * The shape of a variables object with every value replaced by its type: keys survive for triage
 * ("which input keys were sent?"), and no value can leak, whatever new PII field is added later.
 */
export function variableShape(v: unknown, depth = 0): unknown {
  if (v === null) return 'null';
  if (Array.isArray(v)) {
    if (depth >= MAX_DEPTH) return `array(${v.length})`;
    const head = v.slice(0, MAX_ARRAY).map((x) => variableShape(x, depth + 1));
    return v.length > MAX_ARRAY ? [...head, `…${v.length - MAX_ARRAY} more`] : head;
  }
  if (typeof v === 'object') {
    if (depth >= MAX_DEPTH) return 'object';
    return Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, variableShape(x, depth + 1)]));
  }
  return typeof v;
}

/** Deep copy with PII keys redacted and graphql payloads cut from every string. */
export function scrubValue(v: unknown, depth = 0): unknown {
  if (typeof v === 'string') return stripGraphqlPayload(v);
  if (v === null || typeof v !== 'object' || depth >= MAX_DEPTH) return v;
  if (Array.isArray(v)) return v.map((x) => scrubValue(x, depth + 1));
  return Object.fromEntries(
    Object.entries(v as Record<string, unknown>).map(([k, x]) => [
      k,
      k.toLowerCase() === 'variables'
        ? variableShape(x)
        : PII_KEYS.has(k.toLowerCase()) && x != null && typeof x !== 'boolean'
          ? REDACTED
          : scrubValue(x, depth + 1),
    ]),
  );
}

/** `beforeSend`: scrub an outgoing Sentry event in place-safe fashion (returns a scrubbed copy). */
export function scrubSentryEvent(event: ErrorEvent, _hint?: EventHint): ErrorEvent {
  const out: ErrorEvent = { ...event };
  if (typeof out.message === 'string') out.message = stripGraphqlPayload(out.message);
  if (out.exception?.values) {
    out.exception = {
      ...out.exception,
      values: out.exception.values.map((ex) => (
        typeof ex.value === 'string' ? { ...ex, value: stripGraphqlPayload(ex.value) } : ex
      )),
    };
  }
  if (out.extra) out.extra = scrubValue(out.extra) as ErrorEvent['extra'];
  if (out.contexts) {
    out.contexts = Object.fromEntries(
      Object.entries(out.contexts).map(([k, ctx]) => [k, SDK_CONTEXTS.has(k) ? ctx : scrubValue(ctx)]),
    ) as ErrorEvent['contexts'];
  }
  if (out.request?.data !== undefined) out.request = { ...out.request, data: scrubValue(out.request.data) };
  if (out.breadcrumbs) {
    out.breadcrumbs = out.breadcrumbs.map((b) => ({
      ...b,
      ...(typeof b.message === 'string' ? { message: stripGraphqlPayload(b.message) } : {}),
      ...(b.data ? { data: scrubValue(b.data) as typeof b.data } : {}),
    }));
  }
  return out;
}

/**
 * What graphql_client reports for a GraphQL error response: an Error carrying only the operation
 * and the first error message, plus triage context without values.
 */
export function graphqlErrorReport(clientError: ClientError, operationName: string) {
  const errors = clientError.response?.errors ?? [];
  const first = errors[0];
  const code = String(first?.extensions?.code ?? '');
  const errorType = typeof first?.extensions?.errorType === 'string' ? first.extensions.errorType : '';
  const error = new Error(`[GraphQL] ${operationName}: ${stripGraphqlPayload(first?.message ?? 'error')}`);
  error.name = 'GraphQLError';
  return {
    error,
    context: {
      tags: {
        graphql_operation: operationName,
        graphql_error_code: code,
        ...(errorType ? { graphql_error_type: errorType } : {}),
      },
      fingerprint: ['graphql-error', operationName, errorType || code || 'unknown'],
      extra: {
        errors: errors.map((e) => ({
          message: e.message,
          path: e.path?.join('.'),
          code: e.extensions?.code,
          errorType: e.extensions?.errorType,
        })),
        variables: variableShape(clientError.request?.variables),
      },
    },
  };
}
